import { useState } from "react";
import { Check, ChevronsUpDown, Plane, Hotel, MapPin } from "lucide-react";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";

export type LocationOption = {
  id: string;
  name: string;
  lat: number;
  lng: number;
  kind: "airport" | "hotel";
  zone_id?: string | null;
  code?: string;
};

type Props = {
  value: string;
  onChange: (v: string) => void;
  options: LocationOption[];
  exclude?: string;
  placeholder?: string;
  className?: string;
};

// Normalize (strip accents, lowercase) for accent-insensitive search
const norm = (s: string) =>
  s.normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase();

export default function LocationCombobox({
  value,
  onChange,
  options,
  exclude,
  placeholder = "Select location...",
  className,
}: Props) {
  const [open, setOpen] = useState(false);
  const filtered = options.filter((o) => o.id !== exclude);
  const airports = filtered.filter((o) => o.kind === "airport");
  const hotels = filtered.filter((o) => o.kind === "hotel");
  const selected = filtered.find((o) => o.id === value);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          className={cn(
            "w-full h-11 justify-between font-normal text-sm",
            !selected && "text-muted-foreground",
            className
          )}
        >
          <span className="flex items-center gap-2 truncate">
            {selected ? (
              selected.kind === "airport" ? (
                <Plane className="h-4 w-4 shrink-0 text-primary" />
              ) : (
                <Hotel className="h-4 w-4 shrink-0 text-primary" />
              )
            ) : (
              <MapPin className="h-4 w-4 shrink-0" />
            )}
            <span className="truncate">{selected ? selected.name : placeholder}</span>
          </span>
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent
        className="w-[--radix-popover-trigger-width] p-0 bg-popover z-50"
        align="start"
      >
        <Command
          filter={(itemValue, search) => {
            if (norm(itemValue).includes(norm(search))) return 1;
            return 0;
          }}
        >
          <CommandInput placeholder="Search airport or hotel..." className="h-10" />
          <CommandList className="max-h-72">
            <CommandEmpty>No location found.</CommandEmpty>
            {airports.length > 0 && (
              <CommandGroup heading="✈ Airports">
                {airports.map((o) => (
                  <CommandItem
                    key={o.id}
                    value={o.name}
                    onSelect={() => {
                      onChange(o.id);
                      setOpen(false);
                    }}
                  >
                    <Plane className="mr-2 h-4 w-4 text-primary" />
                    <span className="truncate">{o.name}</span>
                    <Check
                      className={cn(
                        "ml-auto h-4 w-4",
                        value === o.id ? "opacity-100" : "opacity-0"
                      )}
                    />
                  </CommandItem>
                ))}
              </CommandGroup>
            )}
            {hotels.length > 0 && (
              <CommandGroup heading="🏨 Hotels & Destinations">
                {hotels.map((o) => (
                  <CommandItem
                    key={o.id}
                    value={o.name}
                    onSelect={() => {
                      onChange(o.id);
                      setOpen(false);
                    }}
                  >
                    <Hotel className="mr-2 h-4 w-4 text-primary" />
                    <span className="truncate">{o.name}</span>
                    <Check
                      className={cn(
                        "ml-auto h-4 w-4",
                        value === o.id ? "opacity-100" : "opacity-0"
                      )}
                    />
                  </CommandItem>
                ))}
              </CommandGroup>
            )}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
